import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";

/**
 * Mounts children inside an open shadow root on a block-level host div.
 * Accepts pre-built CSSStyleSheet objects via `sheets` so callers can share
 * a single parsed sheet across multiple instances (avoids double-parsing).
 */
export function ShadowHost({
  sheets,
  children,
}: {
  sheets: CSSStyleSheet[];
  children: React.ReactNode;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [shadow, setShadow] = useState<ShadowRoot | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || host.shadowRoot) return;
    const root = host.attachShadow({ mode: "open" });
    root.adoptedStyleSheets = sheets;
    setShadow(root);
    // sheets is intentionally read once at mount — callers pass a stable reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={hostRef}>
      {shadow && createPortal(children, shadow)}
    </div>
  );
}
